import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  Button,
  Box,
  Grid,
  Typography,
  FormLabel,
  Paper,
} from '@mui/material';
import { PAYMENT_MODE } from 'utils/constant';
import { Toggle } from 'rsuite';
import InputField from 'components/Input-Field/input-field';
import SelectInput from 'components/Select-Field/select-field';
import SelectAddCombo from 'components/Select-Field/select-add-combo';
import FormElements from 'components/FormElements';

const OperationalExpense = ({ action, categories, expenseForm, saveFormHandler, updateFormHandler }) => {

  const { values,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    errors,
  } = useFormik({
    initialValues: { ...expenseForm },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      expenseName: Yup.string().required('Expense Name is required.'),
      category: Yup.string().min(2).max(20).required('Select Category or Add New Category.'),
      vendor: Yup.string().max(50).required('Vendor Name is required'),
      amount: Yup.number().min(1).max(9999).required('Expense Amount is required.'),
      paid: Yup.number().min(1).max(9999).required('Payment Amount is required.'),
      expenseDate: Yup.string().min(2).max(25).required('Expense Date is required.'),
      dueDate: Yup.string().min(2).max(25).required('Due Date is required.'),
      paymentMode: Yup.string().min(2).max(40).required('Mode of payment is required.'),
    }),

    onSubmit: values => {
      const trimmed_data = {};
      Object.keys(values).forEach(key => {
        if (key === 'paymentMode') {
          trimmed_data[key] = PAYMENT_MODE[values[key]];
        } else {
          trimmed_data[key] = typeof (values[key]) === 'string' ? values[key].trim() : values[key]
        }
      });
      action === 'Save' ? saveFormHandler(trimmed_data) : updateFormHandler(trimmed_data);
    },
  });

  const handleSelectAddComboChange = (evt, newValue) => {
    if (newValue === null) return;
    if (typeof newValue === 'string') {
      handleChange(evt);
      setFieldValue('category', newValue);
    } else if (newValue && newValue.inputValue) {
      // Create a new value from the user input
      handleChange(evt);
      setFieldValue('category', newValue.inputValue);
    } else {
      handleChange(evt);
      setFieldValue('category', newValue.name);
    }
  }

  return (
    <form onSubmit={handleSubmit} autoComplete='off'>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <SelectAddCombo
            label='Expense Category'
            name='category'
            value={values.category}
            variant='outlined'
            fullWidth
            options={categories}
            error={Boolean(touched.category && errors.category)}
            helperText={touched.category && errors.category}
            onBlur={handleBlur}
            onChange={(evt, val) => handleSelectAddComboChange(evt, val)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <InputField
            label='Expense Name'
            name='expenseName'
            value={values.expenseName}
            variant='outlined'
            fullWidth
            error={Boolean(touched.expenseName && errors.expenseName)}
            helperText={touched.expenseName && errors.expenseName}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <InputField
            label='Expense Date'
            name='expenseDate'
            value={values.expenseDate}
            type='date'
            variant='outlined'
            fullWidth
            error={Boolean(touched.expenseDate && errors.expenseDate)}
            helperText={touched.expenseDate && errors.expenseDate}
            onBlur={handleBlur}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <InputField
            label='Due Date'
            name='dueDate'
            value={values.dueDate}
            type='date'
            variant='outlined'
            fullWidth
            error={Boolean(touched.dueDate && errors.dueDate)}
            helperText={touched.dueDate && errors.dueDate}
            onBlur={handleBlur}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <InputField
            label='Vendor Name'
            name='vendor'
            value={values.vendor}
            variant='outlined'
            fullWidth
            error={Boolean(touched.vendor && errors.vendor)}
            helperText={touched.vendor && errors.vendor}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <InputField
            label='Amount'
            name='amount'
            type='number'
            value={values.amount}
            variant='outlined'
            fullWidth
            error={Boolean(touched.amount && errors.amount)}
            helperText={touched.amount && errors.amount}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <InputField
            label='Payment Made'
            name='paid'
            type='number'
            value={values.paid}
            variant='outlined'
            fullWidth
            error={Boolean(touched.paid && errors.paid)}
            helperText={touched.paid && errors.paid}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <SelectInput
            label='Select Payment Mode'
            name='paymentMode'
            select
            value={values.paymentMode}
            variant='outlined'
            fullWidth
            error={Boolean(touched.paymentMode && errors.paymentMode)}
            helperText={touched.paymentMode && errors.paymentMode}
            onBlur={handleBlur}
            onChange={handleChange}
            options={Object.keys(PAYMENT_MODE)}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormElements.SectionTitle title='Additional Information' />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <FormLabel component='legend'>Auto Payment</FormLabel>
          <Toggle
            name='autoPayEnabled'
            value={values.autoPayEnabled}
            checkedChildren='YES'
            unCheckedChildren='NO'
            defaultChecked={values.autoPayEnabled}
            onChange={(val) => setFieldValue('autoPayEnabled', val)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <SelectInput
            label='Recurrence'
            name='recurring'
            select
            value={values.recurring}
            variant='outlined'
            fullWidth
            error={Boolean(touched.recurring && errors.recurring)}
            helperText={touched.recurring && errors.recurring}
            onBlur={handleBlur}
            onChange={handleChange}
            options={['MONTHLY', 'WEEKLY']}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6 }}>
          <InputField
            label='Notes'
            name='notes'
            value={values.notes}
            variant='outlined'
            fullWidth
            error={Boolean(touched.notes && errors.notes)}
            helperText={touched.notes && errors.notes}
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
      <Box
        display='flex'
        justifyContent='flex-end'
        p={2}>
        {action === 'Save' ?
          <Button
            type='submit'
            variant='contained'
            color='primary'
          >
            Save Expense
          </Button> :
          <Button
            type='submit'
            variant='contained'
            color='primary'
          >
            Update Expense
          </Button>
        }
      </Box>
    </form>
  );
};

export default OperationalExpense;